import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full h-10 px-3 bg-white border-2 border-[#3D2008] font-manrope text-sm text-[#3D2008] placeholder-[#5C4A35]/50 focus:outline-none focus:border-[#C4522A] transition-colors";

export const CartDrawer = () => {
    const { cart, cartOpen, setCartOpen, removeItem, updateQty, clearCart, total, count } = useCart();
    const [showCheckout, setShowCheckout] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", address: "", city: "", state: "", zip: "", country: "US" });
    const navigate = useNavigate();

    const update = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

    const handleCheckout = async () => {
        if (!form.name || !form.email || !form.address || !form.city || !form.state || !form.zip) {
            toast.error("Please fill in all shipping fields.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API}/merch/checkout`, {
                items: cart.map(item => ({
                    product_id: item.product_id,
                    variant_id: item.variant_id,
                    product_name: item.product_name,
                    size: item.size,
                    color: item.color,
                    quantity: item.quantity,
                    sync_variant_id: item.sync_variant_id || null,
                })),
                customer_name: form.name,
                customer_email: form.email,
                shipping_address: form.address,
                shipping_city: form.city,
                shipping_state: form.state,
                shipping_zip: form.zip,
                shipping_country: form.country,
                origin_url: window.location.origin,
            });
            window.location.href = res.data.url;
        } catch (e) {
            toast.error("Checkout failed. Please try again.");
            setLoading(false);
        }
    };

    if (!cartOpen) return null;

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/60" onClick={() => setCartOpen(false)} />
            <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#F5EDD6] border-l-4 border-[#3D2008] flex flex-col shadow-[-12px_0_32px_rgba(0,0,0,0.3)]">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-[#3D2008] border-b-2 border-[#C9A84C]">
                    <div className="flex items-center gap-3">
                        <ShoppingBag size={20} className="text-[#C9A84C]" />
                        <span className="font-anton text-[#F5EDD6] uppercase text-lg tracking-wide">
                            Your Cart {count > 0 && `(${count})`}
                        </span>
                    </div>
                    <button
                        onClick={() => setCartOpen(false)}
                        data-testid="cart-close-btn"
                        className="text-[#F5EDD6] hover:text-[#C4522A] transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {cart.length === 0 ? (
                        <div className="text-center py-16">
                            <ShoppingBag size={48} className="text-[#C4522A]/25 mx-auto mb-4" />
                            <p className="font-manrope text-[#5C4A35] text-lg">Your cart is empty</p>
                            <button
                                onClick={() => { setCartOpen(false); navigate("/merch"); }}
                                className="mt-4 font-manrope text-sm text-[#C4522A] underline underline-offset-2"
                            >
                                Browse the shop →
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3 mb-6" data-testid="cart-items">
                                {cart.map(item => (
                                    <div key={item.variant_id} className="flex gap-3 bg-white border-2 border-[#3D2008] p-3 shadow-[3px_3px_0px_0px_rgba(61,32,8,0.15)]">
                                        <img
                                            src={item.thumbnail}
                                            alt={item.product_name}
                                            className="w-16 h-16 object-cover border border-[#3D2008] flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-anton text-sm text-[#3D2008] uppercase leading-tight">{item.product_name}</h4>
                                            <p className="font-manrope text-xs text-[#5C4A35] mt-0.5">{item.color} / {item.size}</p>
                                            <div className="flex items-center justify-between mt-2">
                                                <div className="flex items-center gap-1">
                                                    <button onClick={() => updateQty(item.variant_id, item.quantity - 1)}
                                                        className="w-6 h-6 flex items-center justify-center border border-[#3D2008] hover:bg-[#3D2008] hover:text-white transition-colors text-xs">
                                                        <Minus size={10} />
                                                    </button>
                                                    <span className="font-manrope text-sm font-semibold w-5 text-center">{item.quantity}</span>
                                                    <button onClick={() => updateQty(item.variant_id, item.quantity + 1)}
                                                        className="w-6 h-6 flex items-center justify-center border border-[#3D2008] hover:bg-[#3D2008] hover:text-white transition-colors text-xs">
                                                        <Plus size={10} />
                                                    </button>
                                                </div>
                                                <span className="font-anton text-[#C4522A] text-base">${(item.price * item.quantity).toFixed(2)}</span>
                                            </div>
                                        </div>
                                        <button onClick={() => removeItem(item.variant_id)}
                                            className="text-[#5C4A35] hover:text-[#C4522A] transition-colors self-start flex-shrink-0">
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Total */}
                            <div className="border-t-2 border-[#3D2008] pt-4 mb-4">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-manrope font-semibold text-[#3D2008]">Subtotal</span>
                                    <span className="font-anton text-2xl text-[#C4522A]">${total.toFixed(2)}</span>
                                </div>
                                <p className="font-manrope text-xs text-[#5C4A35]">+ shipping calculated at checkout · printed &amp; shipped by Printful</p>
                            </div>

                            {!showCheckout ? (
                                <button
                                    onClick={() => setShowCheckout(true)}
                                    data-testid="proceed-to-checkout-btn"
                                    className="w-full py-4 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-lg border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150"
                                >
                                    Proceed to Checkout
                                </button>
                            ) : (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-anton text-[#3D2008] uppercase text-lg">Shipping Info</h3>
                                        <button onClick={() => setShowCheckout(false)} className="font-manrope text-xs text-[#5C4A35] underline">← Back</button>
                                    </div>
                                    <div className="space-y-2">
                                        <input type="text" placeholder="Full Name *" value={form.name} onChange={update("name")} required data-testid="checkout-name-input" className={inputCls} />
                                        <input type="email" placeholder="Email Address *" value={form.email} onChange={update("email")} required data-testid="checkout-email-input" className={inputCls} />
                                        <input type="text" placeholder="Street Address *" value={form.address} onChange={update("address")} required data-testid="checkout-address-input" className={inputCls} />
                                        <div className="grid grid-cols-2 gap-2">
                                            <input type="text" placeholder="City *" value={form.city} onChange={update("city")} required className={inputCls} />
                                            <input type="text" placeholder="State *" value={form.state} onChange={update("state")} required className={inputCls} />
                                        </div>
                                        <input type="text" placeholder="ZIP Code *" value={form.zip} onChange={update("zip")} required data-testid="checkout-zip-input" className={inputCls} />
                                    </div>
                                    <button
                                        onClick={handleCheckout}
                                        disabled={loading}
                                        data-testid="complete-purchase-btn"
                                        className="w-full mt-4 py-4 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-lg border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
                                    >
                                        {loading && <Loader2 size={18} className="animate-spin" />}
                                        {loading ? "Redirecting..." : `Pay $${total.toFixed(2)}`}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CartDrawer;
